import * as THREE from 'https://esm.sh/three@0.160.0';
import { GLTFLoader } from 'https://esm.sh/three@0.160.0/examples/jsm/loaders/GLTFLoader.js';

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

class TechTowelScrollModel {
  constructor(section) {
    this.section = section;
    this.mount = section.querySelector('[data-tt-model-canvas]');
    this.status = section.querySelector('[data-tt-model-status]');
    this.features = Array.from(section.querySelectorAll('.tt-po-feature'));
    this.modelSrc = section.dataset.modelSrc;
    this.progress = 0;
    this.modelReady = false;
    this.idleSpin = false;

    if (!this.mount || !this.modelSrc) return;

    this.init();
  }

  init() {
    this.scene = new THREE.Scene();
    this.camera = new THREE.PerspectiveCamera(22, 1, 0.1, 100);
    this.camera.position.set(0, 0.1, 7.4);

    try {
      this.renderer = new THREE.WebGLRenderer({
        alpha: true,
        antialias: true,
        powerPreference: 'high-performance'
      });
    } catch (error) {
      this.mount.classList.add('has-fallback');
      if (this.status) this.status.textContent = '3D preview unavailable';
      return;
    }
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 1.75));
    this.renderer.outputColorSpace = THREE.SRGBColorSpace;
    this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
    this.renderer.toneMappingExposure = 1.02;
    this.mount.appendChild(this.renderer.domElement);

    this.addLights();
    this.loadModel();

    this.onResize = this.resize.bind(this);
    this.onScroll = this.updateScrollState.bind(this);
    window.addEventListener('resize', this.onResize, { passive: true });
    window.addEventListener('scroll', this.onScroll, { passive: true });

    this.resize();
    this.updateScrollState();
    this.animate();
  }

  addLights() {
    const hemi = new THREE.HemisphereLight(0xf8f5ec, 0x111111, 1.65);
    const key = new THREE.DirectionalLight(0xffffff, 2.8);
    const rim = new THREE.DirectionalLight(0xd9ff00, 1.1);

    key.position.set(2.6, 3.4, 4.8);
    rim.position.set(-3.8, 1.8, -3.2);

    this.scene.add(hemi, key, rim);
  }

  loadModel() {
    const loader = new GLTFLoader();

    loader.load(
      this.modelSrc,
      (gltf) => {
        this.model = gltf.scene;
        this.model.rotation.set(0.12, -0.38, -0.04);
        this.model.position.set(0, -0.1, 0);

        const box = new THREE.Box3().setFromObject(this.model);
        const size = box.getSize(new THREE.Vector3());
        const center = box.getCenter(new THREE.Vector3());
        const maxAxis = Math.max(size.x, size.y, size.z);
        const scale = maxAxis > 0 ? 3.25 / maxAxis : 1;

        this.model.scale.setScalar(scale);
        this.model.position.sub(center.multiplyScalar(scale));
        this.model.position.y += 0.58;

        this.model.traverse((child) => {
          if (!child.isMesh) return;
          child.castShadow = false;
          child.receiveShadow = false;
          if (child.material) {
            child.material.side = THREE.DoubleSide;
            child.material.needsUpdate = true;
          }
        });

        this.scene.add(this.model);
        this.modelReady = true;
        this.mount.classList.add('is-loaded');
        const fallback = this.mount.querySelector('.tt-po-model__fallback');
        if (fallback) fallback.hidden = true;
        if (this.status) this.status.textContent = '3D towel ready';
        this.updateScrollState();
      },
      undefined,
      () => {
        this.mount.classList.add('has-fallback');
        if (this.status) this.status.textContent = '3D preview unavailable';
      }
    );
  }

  resize() {
    const rect = this.mount.getBoundingClientRect();
    const width = Math.max(rect.width, 1);
    const height = Math.max(rect.height, 1);
    this.camera.aspect = width / height;
    this.camera.position.z = width < 520 ? 8.2 : 7.2;
    this.camera.updateProjectionMatrix();
    this.renderer.setSize(width, height, false);
  }

  updateScrollState() {
    const rect = this.section.getBoundingClientRect();
    const scrollable = Math.max(rect.height - window.innerHeight, 1);
    this.progress = clamp((window.innerHeight * 0.14 - rect.top) / scrollable, 0, 1);
    this.idleSpin = this.progress > 0.965;

    this.section.style.setProperty('--tt-model-progress', this.progress.toFixed(4));
    this.features.forEach((feature, index) => {
      const revealStart = 0.72 + index * 0.06;
      const reveal = clamp((this.progress - revealStart) / 0.14, 0, 1);
      feature.style.setProperty('--tt-feature-reveal', reveal.toFixed(4));
    });
  }

  animate() {
    requestAnimationFrame(() => this.animate());

    if (this.modelReady && this.model) {
      const scrollRotation = -0.42 + this.progress * Math.PI * 3.8;
      this.model.rotation.y = scrollRotation;
      this.model.rotation.x = 0.08 + Math.sin(this.progress * Math.PI) * 0.12;
      this.model.rotation.z = -0.04 + Math.sin(this.progress * Math.PI * 1.4) * 0.06;

      if (this.idleSpin) {
        this.model.rotation.y += performance.now() * 0.00018;
      }
    }

    this.renderer.render(this.scene, this.camera);
  }
}

document.querySelectorAll('[data-tt-scroll-model]').forEach((section) => {
  new TechTowelScrollModel(section);
});
